import { useState, type ReactNode } from 'react';

import {
  InstallAppModal,
  IosInstallInstructions,
  TovButton,
  TovIcon,
  msg,
} from '@tov-plus/ui';
import { getOfflineCacheResponseMeta } from '@tov-plus/sdk';

import { useConnectivity } from './connectivity';
import { usePwaInstall } from './hooks/usePwaInstall';
import type { PwaInstallAnalyticsHandlers } from './types';

export interface PwaInstallActionCopy {
  ios: {
    addStep: string;
    description: string;
    hint?: string;
    shareStep: string;
  };
  modal: {
    description: string;
    title: string;
  };
}

export interface PwaInstallActionRenderProps {
  disabled: boolean;
  installing: boolean;
  onAction(): void;
  requiresManualInstall: boolean;
}

export interface PwaInstallActionProps {
  analytics?: Partial<PwaInstallAnalyticsHandlers>;
  copy: PwaInstallActionCopy;
  renderAction(input: PwaInstallActionRenderProps): ReactNode;
}

export function PwaInstallAction({
  analytics,
  copy,
  renderAction,
}: PwaInstallActionProps) {
  const [manualInstallOpen, setManualInstallOpen] = useState(false);
  const [installing, setInstalling] = useState(false);
  const { canInstall, install, isInstalled, requiresManualInstall } =
    usePwaInstall({
      analytics,
    });

  if (!canInstall || isInstalled) {
    return null;
  }

  const disabled = !canInstall || installing;

  const onAction = () => {
    void (async () => {
      if (disabled) {
        return;
      }

      if (requiresManualInstall) {
        setManualInstallOpen(true);
        analytics?.onManualInstallOpened?.('ios-safari');
        return;
      }

      setInstalling(true);
      try {
        await install();
      } finally {
        setInstalling(false);
      }
    })();
  };

  return (
    <>
      {renderAction({
        disabled,
        installing,
        onAction,
        requiresManualInstall,
      })}
      <InstallAppModal
        description={copy.modal.description}
        open={manualInstallOpen}
        onOpenChange={setManualInstallOpen}
        title={copy.modal.title}
      >
        <IosInstallInstructions
          addStep={copy.ios.addStep}
          description={copy.ios.description}
          hint={copy.ios.hint}
          shareStep={copy.ios.shareStep}
        />
      </InstallAppModal>
    </>
  );
}

const offlineBannerStyle = {
  alignItems: 'center',
  background: '#fff7ed',
  borderBlockEnd: '1px solid #fed7aa',
  color: '#7c2d12',
  display: 'flex',
  gap: '0.6rem',
  justifyContent: 'center',
  lineHeight: 1.4,
  padding: '0.65rem 1rem',
  position: 'relative',
  width: '100%',
  zIndex: 2,
} as const;

const offlinePanelStyle = {
  alignItems: 'center',
  display: 'grid',
  gap: '0.75rem',
  justifyItems: 'center',
  margin: '0 auto',
  maxWidth: '34rem',
  padding: '2rem',
  textAlign: 'center',
} as const;

const offlineActionMessageStyle = {
  alignItems: 'center',
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  borderRadius: '8px',
  color: '#7c2d12',
  display: 'flex',
  gap: '0.55rem',
  lineHeight: 1.4,
  padding: '0.75rem 0.9rem',
} as const;

const cachedNoticeStyle = {
  alignItems: 'center',
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '8px',
  color: '#1e3a8a',
  display: 'flex',
  gap: '0.55rem',
  lineHeight: 1.4,
  padding: '0.75rem 0.9rem',
} as const;

const staleBadgeStyle = {
  alignItems: 'center',
  background: '#fef3c7',
  border: '1px solid #f59e0b',
  borderRadius: '999px',
  color: '#78350f',
  display: 'inline-flex',
  fontSize: '0.78rem',
  fontWeight: 700,
  gap: '0.35rem',
  lineHeight: 1,
  padding: '0.35rem 0.55rem',
  whiteSpace: 'nowrap',
} as const;

export function OfflineBanner() {
  const connectivity = useConnectivity();

  if (connectivity.isOnline) {
    return null;
  }

  return (
    <div role="status" style={offlineBannerStyle}>
      <TovIcon name="warning-circle" />
      <span>
        {msg(
          'components.offline_banner.you_are_offline_some_features_may_be_unavailable_until_your_connection',
        )}
      </span>
    </div>
  );
}

export interface OfflineStatePanelProps {
  action?: ReactNode;
  detail?: string;
}

export function OfflineStatePanel({ action, detail }: OfflineStatePanelProps) {
  return (
    <section
      aria-live="polite"
      data-testid="offline-state-panel"
      style={offlinePanelStyle}
    >
      <TovIcon name="warning-circle" />
      <h1>
        {msg(
          'components.offline_state_panel.cannot_verify_your_session_while_offline',
        )}
      </h1>
      <p>
        {detail ?? msg('components.offline_state_panel.reconnect_to_continue')}
      </p>
      {action ?? null}
    </section>
  );
}

export interface OfflineActionUnavailableMessageProps {
  message?: string;
}

export function OfflineActionUnavailableMessage({
  message,
}: OfflineActionUnavailableMessageProps) {
  return (
    <div role="status" style={offlineActionMessageStyle}>
      <TovIcon name="warning-circle" />
      <span>
        {message ??
          msg(
            'components.offline_action_unavailable_message.this_action_is_unavailable_while_offline',
          )}
      </span>
    </div>
  );
}

export function OfflineRetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <TovButton className="primary-button" onClick={onRetry}>
      {msg('general.actions.retry')}
    </TovButton>
  );
}

export interface CachedDataNoticeProps {
  cachedValue?: unknown;
}

export function CachedDataNotice({ cachedValue }: CachedDataNoticeProps) {
  const meta = getOfflineCacheResponseMeta(cachedValue);

  if (!meta) {
    return null;
  }

  return (
    <div role="status" style={cachedNoticeStyle}>
      <TovIcon name="warning-circle" />
      <span>
        {meta.isStale
          ? `${msg(
              'components.cached_data_notice.showing_saved_data_from',
            )} ${new Date(meta.savedAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}. ${msg('components.cached_data_notice.reconnect_to_refresh')}`
          : msg(
              'components.cached_data_notice.showing_saved_data_some_details_may_be_outdated',
            )}
      </span>
    </div>
  );
}

export interface StaleDataBadgeProps {
  cachedValue?: unknown;
}

export function StaleDataBadge({ cachedValue }: StaleDataBadgeProps) {
  const meta = getOfflineCacheResponseMeta(cachedValue);

  if (!meta) {
    return null;
  }

  return (
    <span style={staleBadgeStyle}>
      <TovIcon name="warning-circle" />
      {msg('components.stale_data_badge.saved_data')}
    </span>
  );
}

export function OfflineEmptyCacheState() {
  return (
    <OfflineStatePanel
      detail={msg(
        'components.offline_empty_cache_state.this_page_is_unavailable_offline_because_it_has_not_been_loaded',
      )}
    />
  );
}
